const chapter = {
  id: 'inverse-trigonometric-functions',
  slug: 'inverse-trigonometric-functions',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE',
  title: {
    english: 'Inverse Trigonometric Functions',
        hindi: 'प्रतिलोम त्रिकोणमितीय फलन',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1-2 questions in JEE Main / Adv',
        hindi: 'जेईई मेन/एड में 1-2 प्रश्न',
  },
  syllabusTags: ['Trigonometry', 'Class-12', 'JEE Main', 'JEE Advanced'],
  sections: [
    {
      id: 'principal-values',
      title: {
        english: 'Domains, Ranges & Principal Values',
        hindi: 'प्रान्त, परिसर और मुख्य मान',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Trig functions are NOT one-one over $\\mathbb{R}$ → restrict to **principal branches** to define inverses.',
        hindi: 'ट्रिग फ़ंक्शन $\\mathbb{R}$ से अधिक एक-एक नहीं हैं → व्युत्क्रमों को परिभाषित करने के लिए **प्रमुख शाखाओं** तक सीमित हैं।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Function', 'Domain', 'Range (Principal value)'],
          rows: [
            ['$\\sin^{-1} x$', '$[-1, 1]$', '$[-\\pi/2,\\ \\pi/2]$'],
            ['$\\cos^{-1} x$', '$[-1, 1]$', '$[0,\\ \\pi]$'],
            ['$\\tan^{-1} x$', '$\\mathbb{R}$', '$(-\\pi/2,\\ \\pi/2)$'],
            ['$\\cot^{-1} x$', '$\\mathbb{R}$', '$(0,\\ \\pi)$'],
            ['$\\sec^{-1} x$', '$|x| \\ge 1$', '$[0, \\pi] \\setminus \\{\\pi/2\\}$'],
            ['$\\csc^{-1} x$', '$|x| \\ge 1$', '$[-\\pi/2, \\pi/2] \\setminus \\{0\\}$'],
          ],
        },
        {
          type: 'tip',
          body: {
            english: 'Even functions: $\\cos^{-1}(-x) = \\pi - \\cos^{-1} x$, $\\cot^{-1}(-x) = \\pi - \\cot^{-1} x$, $\\sec^{-1}(-x) = \\pi - \\sec^{-1} x$. Odd: $\\sin^{-1}(-x) = -\\sin^{-1} x$, $\\tan^{-1}(-x) = -\\tan^{-1} x$, $\\csc^{-1}(-x) = -\\csc^{-1} x$.',
        hindi: 'सम कार्य: $\\cos^{-1}(-x) = \\pi - \\cos^{-1} x$, $\\cot^{-1}(-x) = \\pi - \\cot^{-1} x$, $\\sec^{-1}(-x) = \\pi - \\sec^{-1} x$। अजीब: $\\sin^{-1}(-x) = -\\sin^{-1} x$, $\\tan^{-1}(-x) = -\\tan^{-1} x$, $\\csc^{-1}(-x) = -\\csc^{-1} x$।',
          },
        },
      ],
    },
    {
      id: 'identities',
      title: {
        english: 'Inverse-Direct Identities',
        hindi: 'प्रतिलोम-प्रत्यक्ष सर्वसमिकाएँ',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\sin^{-1} x + \\cos^{-1} x = \\dfrac{\\pi}{2}, \\quad x \\in [-1, 1]',
        },
        {
          type: 'equation',
          equation: '\\tan^{-1} x + \\cot^{-1} x = \\dfrac{\\pi}{2}, \\quad x \\in \\mathbb{R}',
        },
        {
          type: 'equation',
          equation: '\\sec^{-1} x + \\csc^{-1} x = \\dfrac{\\pi}{2}, \\quad |x| \\ge 1',
        },
        {
          type: 'equation',
          equation: '\\sin^{-1}(1/x) = \\csc^{-1} x, \\quad \\cos^{-1}(1/x) = \\sec^{-1} x, \\quad \\tan^{-1}(1/x) = \\cot^{-1} x \\ (x > 0)',
        },
        {
          type: 'note',
          body: {
            english: 'For $x < 0$: $\\tan^{-1}(1/x) = \\cot^{-1} x - \\pi$.',
        hindi: '$x < 0$ के लिए: $\\tan^{-1}(1/x) = \\cot^{-1} x - \\pi$.',
          },
        },
      ],
    },
    {
      id: 'sum-formulas',
      title: {
        english: 'Sum & Difference Formulas',
        hindi: 'योग और अन्तर सूत्र',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\tan^{-1} x + \\tan^{-1} y = \\begin{cases} \\tan^{-1}\\dfrac{x+y}{1-xy}, & xy < 1 \\\\ \\pi + \\tan^{-1}\\dfrac{x+y}{1-xy}, & xy > 1,\\ x,y > 0 \\\\ -\\pi + \\tan^{-1}\\dfrac{x+y}{1-xy}, & xy > 1,\\ x,y < 0 \\end{cases}',
        },
        {
          type: 'equation',
          equation: '\\tan^{-1} x - \\tan^{-1} y = \\tan^{-1} \\dfrac{x - y}{1 + xy} \\quad (xy > -1)',
        },
        {
          type: 'equation',
          equation: '\\sin^{-1} x + \\sin^{-1} y = \\sin^{-1}\\big(x\\sqrt{1-y^2} + y\\sqrt{1-x^2}\\big) \\quad (\\text{if } x^2 + y^2 \\le 1)',
        },
        {
          type: 'equation',
          equation: '\\cos^{-1} x + \\cos^{-1} y = \\cos^{-1}\\big(xy - \\sqrt{(1-x^2)(1-y^2)}\\big) \\quad (\\text{if } x + y \\ge 0)',
        },
      ],
    },
    {
      id: 'multiple-angle',
      title: {
        english: 'Multiple-Angle (Double / Triple) Formulas',
        hindi: 'बहु-कोण (द्विगुण/त्रिगुण) सूत्र',
      },
      blocks: [
        {
          type: 'equation',
          equation: '2 \\tan^{-1} x = \\begin{cases} \\tan^{-1}\\dfrac{2x}{1-x^2}, & |x| < 1 \\\\ \\sin^{-1}\\dfrac{2x}{1+x^2}, & |x| \\le 1 \\\\ \\cos^{-1}\\dfrac{1-x^2}{1+x^2}, & x \\ge 0 \\end{cases}',
        },
        {
          type: 'equation',
          equation: '3 \\sin^{-1} x = \\sin^{-1}(3x - 4x^3) \\quad (-1/2 \\le x \\le 1/2)',
        },
        {
          type: 'equation',
          equation: '3 \\cos^{-1} x = \\cos^{-1}(4x^3 - 3x) \\quad (1/2 \\le x \\le 1)',
        },
        {
          type: 'equation',
          equation: '3 \\tan^{-1} x = \\tan^{-1}\\dfrac{3x - x^3}{1 - 3x^2} \\quad \\left( -\\dfrac{1}{\\sqrt 3} < x < \\dfrac{1}{\\sqrt 3} \\right)',
        },
        {
          type: 'warning',
          body: {
            english: 'These formulas have **branch restrictions**. Outside the valid range, add/subtract $\\pi$ (or $\\pi/2$) to correct.',
        hindi: 'इन सूत्रों में **शाखा प्रतिबंध** हैं। मान्य सीमा के बाहर, सही करने के लिए add/subtract $\\pi$ (या $\\pi/2$)।',
          },
        },
      ],
    },
    {
      id: 'examples',
      title: {
        english: 'Worked Examples',
        hindi: 'हल किये गये उदाहरण',
      },
      blocks: [
        {
          type: 'example',
          problem: {
            english: 'Evaluate $\\sin\\big(2 \\tan^{-1}(1/3)\\big)$.',
        hindi: '$\\sin\\big(2 \\tan^{-1}(1/3)\\big)$ का मूल्यांकन करें।',
          },
          solution: {
            english: 'Using $\\sin(2 \\tan^{-1} x) = \\dfrac{2x}{1+x^2}$ with $x=1/3$: $\\dfrac{2/3}{1+1/9} = \\dfrac{2/3}{10/9} = \\dfrac{6}{10} = \\dfrac{3}{5}$.',
        hindi: '$\\sin(2 \\tan^{-1} x) = \\dfrac{2x}{1+x^2}$ को $x=1/3$ के साथ उपयोग करने पर: $\\dfrac{2/3}{1+1/9} = \\dfrac{2/3}{10/9} = \\dfrac{6}{10} = \\dfrac{3}{5}$.',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Solve $\\tan^{-1}(2x) + \\tan^{-1}(3x) = \\pi/4$.',
        hindi: '$\\tan^{-1}(2x) + \\tan^{-1}(3x) = \\pi/4$ को हल करें.',
          },
          solution: {
            english: '$\\tan^{-1}\\dfrac{5x}{1-6x^2} = \\pi/4 \\Rightarrow 5x = 1-6x^2 \\Rightarrow 6x^2 + 5x - 1 = 0 \\Rightarrow x = 1/6,\\ -1$. Discard $x = -1$ (would push outside principal range; check $xy = -6 < 1$ for $x=1/6$ ✓). **$x = 1/6$**.',
        hindi: '$\\tan^{-1}\\dfrac{5x}{1-6x^2} = \\pi/4 \\Rightarrow 5x = 1-6x^2 \\Rightarrow 6x^2 + 5x - 1 = 0 \\Rightarrow x = 1/6,\\ -1$. $x = -1$ को हटा दें (मुख्य सीमा से बाहर चला जाएगा; $x=1/6$ ✓ के लिए $xy = -6 < 1$ की जांच करें)। **$x = 1/6$**.',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Prove $\\sin^{-1}\\dfrac{4}{5} + \\sin^{-1}\\dfrac{5}{13} + \\sin^{-1}\\dfrac{16}{65} = \\dfrac{\\pi}{2}$.',
        hindi: 'सिद्ध करें $\\sin^{-1}\\dfrac{4}{5} + \\sin^{-1}\\dfrac{5}{13} + \\sin^{-1}\\dfrac{16}{65} = \\dfrac{\\pi}{2}$.',
          },
          solution: {
            english: 'Take $\\sin^{-1}(4/5) + \\sin^{-1}(5/13) = \\sin^{-1}\\big(\\dfrac{4}{5}\\cdot\\dfrac{12}{13} + \\dfrac{5}{13}\\cdot\\dfrac{3}{5}\\big) = \\sin^{-1}(63/65)$. Then $\\sin^{-1}(63/65) + \\sin^{-1}(16/65) = \\pi/2$ since $63^2 + 16^2 = 65^2$.',
        hindi: '$\\sin^{-1}(4/5) + \\sin^{-1}(5/13) = \\sin^{-1}\\big(\\dfrac{4}{5}\\cdot\\dfrac{12} फिर $\\sin^{-1}(63/65) + \\sin^{-1}(16/65) = \\pi/2$ चूँकि $63^2 + 16^2 = 65^2$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\sin^{-1} x + \\cos^{-1} x = \\pi/2',
      desc: {
        english: '',
        hindi: '-1 एक्स + -1 एक्स = /2',
      },
    },
    {
      latex: '\\tan^{-1} x + \\cot^{-1} x = \\pi/2',
      desc: {
        english: '',
        hindi: '-1 एक्स + -1 एक्स = /2',
      },
    },
    {
      latex: '\\sec^{-1} x + \\csc^{-1} x = \\pi/2',
      desc: {
        english: '',
        hindi: '-1 एक्स + -1 एक्स = /2',
      },
    },
    {
      latex: '2 \\tan^{-1} x = \\tan^{-1}\\dfrac{2x}{1-x^2} \\ (|x|<1)',
      desc: {
        english: '',
        hindi: '2 -1 x = -1 1-x 2 \\ (|x|<1)',
      },
    },
    {
      latex: '\\tan^{-1} x + \\tan^{-1} y = \\tan^{-1}\\dfrac{x+y}{1-xy}',
      desc: {
        english: '($xy < 1$)',
        hindi: '($xy < 1$)',
      },
    },
    {
      latex: '\\dfrac{d}{dx}\\sin^{-1} x = \\dfrac{1}{\\sqrt{1-x^2}}',
      desc: {
        english: '',
        hindi: 'डीएक्स -1 एक्स =',
      },
    },
    {
      latex: '\\dfrac{d}{dx}\\tan^{-1} x = \\dfrac{1}{1+x^2}',
      desc: {
        english: '',
        hindi: 'डीएक्स -1 एक्स = 1+एक्स 2',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Identity',
      problem: {
        english: 'If $\\tan^{-1}(1/2) + \\tan^{-1}(1/3) = ?$',
        hindi: 'यदि $\\tan^{-1}(1/2) + \\tan^{-1}(1/3) =?$',
      },
      answer: {
        english: '$\\pi/4$',
        hindi: '$\\pi/4$',
      },
      solution: {
        english: '$\\dfrac{1/2 + 1/3}{1 - 1/6} = \\dfrac{5/6}{5/6} = 1 \\Rightarrow \\tan^{-1} 1 = \\pi/4$.',
        hindi: '$\\dfrac{1/2 + 1/3}{1 - 1/6} = \\dfrac{5/6}{5/6} = 1 \\Rightarrow \\tan^{-1} 1 = \\pi/4$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Equation',
      problem: {
        english: 'Solve $\\sin^{-1}(2x) + \\sin^{-1} x = \\pi/2$.',
        hindi: '$\\sin^{-1}(2x) + \\sin^{-1} x = \\pi/2$ को हल करें.',
      },
      answer: {
        english: '$x = 1/\\sqrt 5$',
        hindi: '$x = 1/\\sqrt 5$',
      },
      solution: {
        english: '$\\sin^{-1}(2x) = \\pi/2 - \\sin^{-1} x = \\cos^{-1} x$. So $2x = \\sqrt{1-x^2} \\Rightarrow 4x^2 = 1 - x^2 \\Rightarrow x = 1/\\sqrt 5$.',
        hindi: '$\\sin^{-1}(2x) = \\pi/2 - \\sin^{-1} x = \\cos^{-1} x$. तो $2x = \\sqrt{1-x^2} \\Rightarrow 4x^2 = 1 - x^2 \\Rightarrow x = 1/\\sqrt 5$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Adv',
      tag: 'Range',
      problem: {
        english: 'Find the principal value of $\\cos^{-1}(\\cos(7\\pi/6))$.',
        hindi: '$\\cos^{-1}(\\cos(7\\pi/6))$ का मूल मान ज्ञात कीजिए।',
      },
      answer: {
        english: '$5\\pi/6$',
        hindi: '$5\\pi/6$',
      },
      solution: {
        english: '$\\cos(7\\pi/6) = \\cos(2\\pi - 7\\pi/6) = \\cos(5\\pi/6)$ which lies in $[0, \\pi]$.',
        hindi: '$\\cos(7\\pi/6) = \\cos(2\\pi - 7\\pi/6) = \\cos(5\\pi/6)$ जो $[0, \\pi]$ में निहित है।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Triple',
      problem: {
        english: 'If $\\sin^{-1}(3/5) + \\sin^{-1}(4/5) = ?$',
        hindi: 'यदि $\\sin^{-1}(3/5) + \\sin^{-1}(4/5) =?$',
      },
      answer: {
        english: '$\\pi/2$',
        hindi: '$\\pi/2$',
      },
      solution: {
        english: '$3^2 + 4^2 = 5^2$ → complementary angles.',
        hindi: '$3^2 + 4^2 = 5^2$ → पूरक कोण।',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'Sum',
      problem: {
        english: 'Evaluate $\\tan^{-1}\\dfrac{1}{4} + \\tan^{-1}\\dfrac{2}{9}$.',
        hindi: '$\\tan^{-1}\\dfrac{1}{4} + \\tan^{-1}\\dfrac{2}{9}$ का मूल्यांकन करें.',
      },
      answer: {
        english: '$\\tan^{-1}\\dfrac{1}{2}$',
        hindi: '$\\tan^{-1}\\dfrac{1}{2}$',
      },
      solution: {
        english: '$\\dfrac{1/4 + 2/9}{1 - 2/36} = \\dfrac{17/36}{34/36} = 1/2$.',
        hindi: '$\\dfrac{1/4 + 2/9}{1 - 2/36} = \\dfrac{17/36}{34/36} = 1/2$.',
      },
    },
  ],
  next: {
    slug: 'hyperbolic-functions',
    title: 'Hyperbolic Functions',
  },
};

export default chapter;
